import DS from 'ember-data';
import Model from '@ember-data/model';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { gt } from '@ember/object/computed';
import attr from 'ember-data/attr';
import moment from 'moment-timezone';
import {
  MessagingChannel,
  MessagingChannelEmail,
  MessagingChannelInApp,
  MessagingChannelPush,
  MessagingChannelSlack,
  MessagingChannelTwilio,
  MessagingChannelType,
  MessagingChannelWebhook,
} from 'cio/utils/setup-guide/messaging-channels';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import EnvironmentService from 'cio/services/environment';
import { isEmpty } from '@ember/utils';

export type IntegrationMethod =
  | 'api'
  | 'data-pipelines'
  | 'segment'
  | 'snippet'
  | 'import-csv'
  | 'forms'
  | 'sql-import';
export type IntegrationStatus = 'complete' | 'connected' | 'pending';
export type SetupGuideStep = 'sources' | 'channels' | 'create-campaign' | 'send-campaign';

export const STEPS: SetupGuideStep[] = ['sources', 'channels', 'create-campaign', 'send-campaign'];

function findChannel(type: MessagingChannelType) {
  return computed('messagingChannels.@each.type', function (this: SetupGuideModel) {
    return this.messagingChannels.find((channel) => channel.type === type);
  });
}

function createDefaultChannel(type: MessagingChannelType): MessagingChannel {
  let selectedAt = moment().unix();

  switch (type) {
    case MessagingChannelType.Push:
      return { type, selectedAt, ios: {}, android: {} };
    case MessagingChannelType.Email:
      return { type, selectedAt, skippedLinkTrackingForDomains: [] };
    case MessagingChannelType.Twilio:
      return { type, selectedAt };
    case MessagingChannelType.Webhook:
      return { type, selectedAt };
    case MessagingChannelType.Slack:
      return { type, selectedAt };
    case MessagingChannelType.InApp:
      return { type, selectedAt };
  }
}

type MessageType = 'campaign' | 'newsletter';

export type SetupGuideUseCase =
  | 'onboard_or_retain'
  | 'transactional'
  | 'leads'
  | 'newsletters'
  | 'internal_automations'
  | 'other';
export type SetupGuideDataPipelinesUseCase = 'costs' | 'integration' | 'data_quality' | 'checking_out';

export default class SetupGuideModel extends Model {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  @attr('string') useCase!: SetupGuideUseCase | SetupGuideDataPipelinesUseCase;

  @attr('number') startedAt!: number;
  @attr('number') completedAt!: number;

  @attr('string') integrationMethod!: IntegrationMethod;
  @attr('number') integrationConnectedAt!: number;
  @attr('number') integrationCompletedAt!: number;

  @attr('number') firstMessageCreatedAt!: number;
  @attr('number') firstMessageId!: number;
  @attr('string') firstMessageType!: MessageType;
  @attr('boolean', { defaultValue: false }) personalizationChangeStepsOrder!: boolean;

  @attr('setup-guide/messaging-channels', { defaultValue: () => [] }) messagingChannels!: MessagingChannel[];

  @computed('integrationConnectedAt', 'integrationCompletedAt')
  get integrationStatus(): IntegrationStatus {
    if (!this.integrationMethod) {
      return 'pending';
    }

    if (this.integrationCompletedAt > 0) {
      return 'complete';
    }

    if (this.integrationConnectedAt > 0) {
      return 'connected';
    }

    return 'pending';
  }

  get messagingChannelTypes(): MessagingChannelType[] {
    return this.messagingChannels.map((channel) => channel.type);
  }

  @computed('messagingChannels.{[],@each.completedAt}')
  get everyMessagingChannelComplete() {
    return (
      this.messagingChannels.length > 0 &&
      this.messagingChannels.every((channel) => this.isChannelDoneWithSetup(channel))
    );
  }

  get anyMessagingChannelComplete() {
    return (
      this.messagingChannels.length > 0 && this.messagingChannels.any((channel) => this.isChannelDoneWithSetup(channel))
    );
  }

  @findChannel(MessagingChannelType.Email) email!: MessagingChannelEmail;
  @findChannel(MessagingChannelType.Push) push!: MessagingChannelPush;
  @findChannel(MessagingChannelType.Twilio) twilio!: MessagingChannelTwilio;
  @findChannel(MessagingChannelType.Slack) slack!: MessagingChannelSlack;
  @findChannel(MessagingChannelType.Webhook) webhook!: MessagingChannelWebhook;
  @findChannel(MessagingChannelType.InApp) in_app!: MessagingChannelInApp;

  markChannelComplete(type: MessagingChannelType) {
    let found = false;

    this.messagingChannels = this.messagingChannels.map((channel) => {
      if (channel.type !== type) {
        return channel;
      }

      found = true;

      return {
        ...channel,
        completedAt: moment().unix(),
      };
    });

    if (!found) {
      throw new Error(`channel ${type} not found`);
    }

    return this.save();
  }

  addChannel(type: MessagingChannelType) {
    let hasChannel = this.hasChannel(type);
    if (!hasChannel) {
      this.messagingChannels = [...this.messagingChannels, createDefaultChannel(type)];
    }
    return this.save();
  }

  addChannels(types: MessagingChannelType[]) {
    types.forEach((type) => {
      let hasChannel = this.hasChannel(type);
      if (!hasChannel) {
        this.messagingChannels = [...this.messagingChannels, createDefaultChannel(type)];
      }
    });
    return this.save();
  }

  updateChannel(channel: MessagingChannel) {
    this.messagingChannels = this.messagingChannels.map((c) => (c.type === channel.type ? channel : c));
  }

  hasChannel(type: MessagingChannelType) {
    return this.messagingChannels.some((channel: MessagingChannel) => channel.type === type);
  }

  isChannelDoneWithSetup(channel: MessagingChannel) {
    return !isEmpty(channel.completedAt) || this.environment.current.enabledActions.includes(channel.type);
  }

  // clear campaign/newsletter that was created/sent as part of the setup guide: this is done after it's no longer available due to the user deleting it
  clearFirstMessage() {
    this.setProperties({
      firstMessageCreatedAt: null,
      firstMessageId: null,
      firstMessageType: null,
    });

    return this.save();
  }

  assignFirstMessage(model: CampaignModel | NewsletterModel) {
    let firstMessageType =
      (model as unknown as { constructor: { modelName: string } }).constructor.modelName === 'campaign'
        ? 'campaign'
        : 'newsletter';

    this.setProperties({
      firstMessageCreatedAt: model.created || moment().unix(),
      firstMessageId: model.id,
      firstMessageType,
    });

    return this.save();
  }

  // This is called in the route hooks using setup guide before "firstMessage" is accessed, so it can
  // access campaign/newsletter already loaded into the store:
  loadFirstMessage() {
    if (!this.firstMessageId) {
      return;
    }

    if (this.firstMessageType === 'newsletter') {
      return this.store.findRecord('newsletter', this.firstMessageId);
    } else {
      return this.store.findRecord('campaign', this.firstMessageId);
    }
  }

  @computed('firstMessageType', 'firstMessageId')
  get firstMessage() {
    if (!this.firstMessageId) {
      return;
    }

    if (this.firstMessageType === 'newsletter') {
      return this.store.peekRecord('newsletter', this.firstMessageId);
    } else {
      return this.store.peekRecord('campaign', this.firstMessageId);
    }
  }

  @computed('firstMessageType', 'firstMessage.{sentAt,draft}')
  get firstMessageSent(): boolean {
    if (!this.firstMessageId || this.firstMessage == null) {
      return false;
    }

    if (this.firstMessageType === 'newsletter') {
      return ((this.firstMessage as NewsletterModel).sentAt ?? 0) > 0;
    } else {
      return !(this.firstMessage as CampaignModel).isDraft ?? false;
    }
  }

  @gt('firstMessageCreatedAt', 0) firstMessageCreated!: boolean;

  @computed('integrationStatus', 'everyMessagingChannelComplete', 'firstMessageCreatedAt', 'firstMessageSent')
  get completedSteps(): Record<SetupGuideStep, boolean> {
    // If a message has been sent, we can consider all the steps within the setup guide complete.

    // Each of the steps on the setup guide - integrating a source, setting up a channel, sending a campaign -
    // can be completed outside of the setup guide. If a first message is sent, that's an indication that the setup
    // is complete, and in that case, we can mark each step as complete.
    if (this.firstMessageSent) {
      return {
        sources: true,
        channels: true,
        'create-campaign': true,
        'send-campaign': true,
      };
    }

    return {
      sources: this.integrationStatus === 'complete',
      channels: this.everyMessagingChannelComplete,
      'create-campaign': this.firstMessageCreated,
      'send-campaign': this.firstMessageSent,
    };
  }

  @computed('completedSteps.{sources,channels,create-campaign,send-campaign}')
  get readyToComplete(): boolean {
    return Object.keys(this.completedSteps).every((key: SetupGuideStep) => this.completedSteps[key]);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'setup-guide': SetupGuideModel;
  }
}
